import React, { useState } from "react";
import { useData } from "../../hooks/useData.js";
import { apiFetch } from "../../api/index.js";
import { Button, Modal, FormInput, LoadingSpinner, ConfirmModal } from "../../components/Common.jsx";
import { DynamicIcon } from "../../components/icons.jsx";
import { notify } from '@/components/core';

const FeaturesTab = ({ token }) => {
  const { data: features, isLoading, error, refresh } = useData("/plans/features", token);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const [form, setForm] = useState({
    featureName: "",
    code: "",
    featureDescription: "",
    category: "",
    isConfigurable: true
  });

  const openCreate = () => {
    setEditing(null);
    setForm({
      featureName: "",
      code: "",
      featureDescription: "",
      category: "",
      isConfigurable: true
    });
    setModalOpen(true);
  };

  const openEdit = (feature) => {
    setEditing(feature);
    setForm({
      featureName: feature.featureName || feature.name || "",
      code: feature.code || feature.featureId || "",
      featureDescription: feature.featureDescription || feature.description || "",
      category: feature.category || "",
      isConfigurable: feature.isConfigurable ?? true
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      // Logic to determine ID: Try featureId, then code (fallback if different schema)
      const id = editing?.featureId || editing?.code;
      const method = editing ? "PUT" : "POST";
      const url = editing ? `/plans/features/${id}` : "/plans/features";

      if (editing && !id) {
        throw new Error("Feature ID is missing for update.");
      }

      // Map form to backend schema (code/name/description vs featureName/featureDescription)
      // The seeding script used 'code', 'name', 'description'. 
      // The UI form uses 'featureName', 'featureDescription'. 
      // We should probably normalize this. Let's send both to be safe or map it.
      // Based on previous error "PUT /api/v1/plans/features/undefined", the ID was missing.

      const payload = {
        ...form,
        // If creating, generating a code from name if specific code field isn't there? 
        // Or backend handles it? Let's assume backend handles or we add a 'code' field.
        // For now, let's just send the form.
        name: form.featureName,
        description: form.featureDescription
      };

      await apiFetch(url, token, {
        method,
        body: JSON.stringify(payload),
      });

      notify.success(editing ? "Feature updated" : "Feature created");
      setModalOpen(false);
      refresh();
    } catch (err) {
      console.error(err);
      notify.error(err?.message || "Failed to save feature");
    }
  };

  const handleDelete = async (f) => {
    try {
      const id = f.featureId || f.code;
      if (!id) throw new Error("Feature ID missing");

      await apiFetch(`/plans/features/${id}`, token, { method: "DELETE" });
      notify.success("Feature deleted");
      refresh();
    } catch (err) {
      notify.error(err?.message || "Failed to delete feature");
    } finally {
      setDeleteItem(null);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-600">Failed to load features.</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Features Directory</h2>
        <Button onClick={openCreate}>
          <DynamicIcon name="PlusCircle" className="mr-2" /> Add Feature
        </Button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Feature Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Feature ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {features.map((f, idx) => {
              // Normalizing data access
              const name = f.featureName || f.name;
              const desc = f.featureDescription || f.description;
              const id = f.featureId || f.code;

              return (
                <tr key={id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 rounded bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                        <DynamicIcon name="Zap" className="h-4 w-4" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{name}</div>
                        {f.code && <div className="text-xs text-gray-400 font-mono">{f.code}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 font-mono">
                      {f.code || f.featureId || "-"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-md truncate">
                    {desc}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEdit(f)}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                        title="Edit"
                      >
                        <DynamicIcon name="Edit" className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteItem(f)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <DynamicIcon name="Trash2" className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {features.length === 0 && (
              <tr>
                <td colSpan="4" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  No features found. create one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={modalOpen} title={editing ? "Edit Feature" : "Create Feature"} onClose={() => setModalOpen(false)}>
        <div className="space-y-4">
          {!editing && (
            <p className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
              Note: Feature ID/Code will be auto-generated from the name if not supported by the backend yet.
            </p>
          )}

          <FormInput
            label="Feature Name"
            value={form.featureName}
            onChange={(e) => setForm({ ...form, featureName: e.target.value })}
            placeholder="e.g. Dashboard Studio"
          />

          <FormInput
            label="Feature ID (Code) - Required"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            placeholder="e.g. dashboard_studio"
            disabled={!!editing}
            helperText="Unique system identifier. Cannot be changed once created."
          />

          <FormInput
            label="Description"
            value={form.featureDescription}
            onChange={(e) => setForm({ ...form, featureDescription: e.target.value })}
            placeholder="Brief description of the feature"
          />

          <FormInput
            label="Category (Optional)"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            placeholder="e.g. Studio, Access, Limits"
          />

          <div className="flex justify-end pt-4">
            <Button onClick={handleSave}>
              {editing ? "Save Changes" : "Create Feature"}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={!!deleteItem}
        title={`Delete ${deleteItem?.featureName || deleteItem?.name}?`}
        onClose={() => setDeleteItem(null)}
        onConfirm={() => handleDelete(deleteItem)}
      >
        Are you sure you want to delete this feature? <br />
        <span className="text-sm text-red-500">This might affect plans dependent on this feature.</span>
      </ConfirmModal>
    </div>
  );
};

export default FeaturesTab;
